// @flow

import React, {useMemo} from 'react';
import {Table, Tooltip, Button} from 'antd';

import ld from 'lodash';
import type {Attribute, Hero, Weapon} from '../data/heroes';
import TeamOptimizerOptions from '../components/team_optimizer_options';
import params from '../utils/team_optimizer_params';
import {withRouter} from 'react-router';
import calculator from '../utils/stats_calculator';
import type {Location} from 'react-router';
import type {Calculation, HeroCalculation} from '../utils/stats_calculator';
import CalculationResultDialog from '../components/CalculationResultDialog';
import {Link} from "react-router-dom";

type Team = {
  members: Array<Hero>,
  calculation: Array<HeroCalculation>,
  atk: { basic: number, adjusted: number },
  hp: { basic: number, adjusted: number },
  def: { basic: number, adjusted: number },
  heal: { basic: number, adjusted: number }
};

function generateTeams(
  requiredHeroes: Array<Hero>,
  optionalHeroes: Array<Hero>,
  teams: Array<Team>,
  team: Team,
) {
  const maxCount = Math.min(
    new Set([...requiredHeroes, ...optionalHeroes]
      .map((hero: Hero) => hero.name)).size
    , 4);
  if (requiredHeroes.length + team.members.length >= maxCount) {
    team.members = requiredHeroes.concat(team.members);
    if (team.members.length !== 0) {
      team.calculation = calculator.calculate(team.members);
      teams.push(team);
    }
    return;
  }
  for (const hero of optionalHeroes) {
    if (requiredHeroes.map((h: Hero) => h.name).includes(hero.name)) {
      continue;
    }
    const newTeam = ld.cloneDeep(team);
    if (newTeam.members.every((member: Hero) => member.name < hero.name)) {
      newTeam.members.push(hero);
      generateTeams(requiredHeroes, optionalHeroes, teams, newTeam);
    }
  }
}

function calculateTeamAttributes(teams: Array<Team>) {
  for (const team of teams) {
    const multipliers = new Map<string, Map<Attribute, number>>();
    for (const skilledMember of team.members) {
      for (const member of team.members) {
        for (const skill of skilledMember.skills) {
          const eligible = (
              skill.target === 'SELF' &&
              skilledMember.name === member.name
            ) ||
            member.element === skill.target ||
            (
              skill.target === 'RANGED' &&
              member.weapons.some(
                (weapon: Weapon) => {
                  return [
                    'Bow',
                    'Staff',
                    'Rifle',
                    'Basket',
                  ].includes(weapon);
                })
            ) || (
              skill.target === 'MELEE' &&
              member.weapons.some(
                (weapon: Weapon) => {
                  return [
                    'OneHandedSword',
                    'TwoHandedSword',
                    'Claw',
                    'Gauntlet'].includes(weapon);
                })
            ) ||
            skill.target === 'PARTY';
          if (eligible) {
            if (!multipliers.has(member.name)) {
              multipliers.set(member.name, new Map());
            }
            const memberMap = multipliers.get(member.name);
            if (memberMap != null) {
              if (!memberMap.has(skill.attribute)) {
                memberMap.set(skill.attribute, 0);
              }
              const attr = memberMap.get(skill.attribute);
              if (attr != null) {
                memberMap.set(skill.attribute, attr + skill.value);
              }
            }
          }
        }
      }
    }
    for (const attr of ['ATK', 'HP', 'DEF', 'HEAL']) {
      const {basic, adjusted} = team.members.map((member: Hero) => {
        const basic = member[attr.toLowerCase()];
        const multiplier = 1 + (multipliers.get(member.name)?.get(attr) ?? 0);
        const adjusted = multiplier * basic;
        return {basic, adjusted};
      })
        .reduce(
          (sum: {
            basic: number,
            adjusted: number
          }, value: {
            basic: number,
            adjusted: number
          }) => {
            return {
              basic: sum.basic + value.basic,
              adjusted: sum.adjusted + value.adjusted,
            };
          }, {basic: 0, adjusted: 0});
      team[attr.toLowerCase()].basic = Math.round(basic);
      team[attr.toLowerCase()].adjusted = Math.round(adjusted);
    }
  }
}

function getColumns(
  history: History,
  requiredHeroes: Array<Hero>,
  optionalHeroes: Array<Hero>,
) {
  return [
    {
      title: 'Members',
      dataIndex: 'members',
      key: 'members',
      render: (heroes: Array<Hero>) => {
        return heroes.map((hero: Hero, i: number) => {
            return <div key={i}><Link
              to={'/heroes/' + hero.name}
              target={'_blank'}
            >{hero.name}</Link></div>;
          },
        );
      },
    },
    {
      title: 'Level',
      key: 'level',
      render: () => 68,
    },
    {
      title: 'Effective Attack',
      dataIndex: 'calculation',
      key: 'effectiveAtk',
      // eslint-disable-next-line react/display-name
      render: (result: Array<HeroCalculation>) => {
        return <Button type={'link'} onClick={() => {
          params.update(history, {
            requiredHeroes,
            optionalHeroes,
            selectedHeroes: result.map((r: HeroCalculation) => r.hero)
          });
        }}>{result.flatMap(
          (r: HeroCalculation) => r.atkCalculation
            .map((cal: Calculation) => cal.value),
        ).reduce((a: number, b: number) => a + b, 0)}</Button>;
      },
      defaultSortOrder: 'descend',
      sorter: (
        a: Team,
        b: Team,
      ) => {
        return a.calculation
            .map((c: HeroCalculation) => c.atkTotal)
            .reduce(
              (c: number, d: number) => c + d, 0) -
          b.calculation
            .map((c: HeroCalculation) => c.atkTotal)
            .reduce((c: number, d: number) => c + d, 0);
      },
    },
    {
      title: 'HP',
      dataIndex: 'hp',
      key: 'hp',
      // eslint-disable-next-line react/display-name
      render: ({basic, adjusted}: { basic: number, adjusted: number }) => {
        return <Tooltip title={`${basic} ${adjusted}`}>
          {adjusted}
        </Tooltip>;
      },
      sorter: (
        a: { hp: { basic: number, adjusted: number } },
        b: { hp: { basic: number, adjusted: number } },
      ) => a.hp.adjusted - b.hp.adjusted,
    },
    {
      title: 'Defence',
      dataIndex: 'def',
      key: 'def',
      // eslint-disable-next-line react/display-name
      render: ({basic, adjusted}: { basic: number, adjusted: number }) => {
        return <Tooltip title={`${basic} ${adjusted}`}>
          {adjusted}
        </Tooltip>;
      },
      sorter: (
        a: { def: { basic: number, adjusted: number } },
        b: { def: { basic: number, adjusted: number } },
      ) => a.def.adjusted - b.def.adjusted,
    }, {
      title: 'Heal',
      dataIndex: 'heal',
      key: 'heal',
      // eslint-disable-next-line react/display-name
      render: ({basic, adjusted}: { basic: number, adjusted: number }) => {
        return <Tooltip title={`${basic} ${adjusted}`}>
          {adjusted}
        </Tooltip>;
      },
      sorter: (
        a: { heal: { basic: number, adjusted: number } },
        b: { heal: { basic: number, adjusted: number } },
      ) => a.heal.adjusted - b.heal.adjusted,
    },
    {
      title: 'Roles',
      dataIndex: 'members',
      key: 'roles',
      render: (heroes: Array<Hero>) => {
        return heroes.map((hero: Hero, i: number) => {
          return <div key={i}>{hero.role}</div>;
        });
      },
    },
    {
      title: 'Skills',
      dataIndex: 'members',
      key: 'skills',
      render: (heroes: Array<Hero>) => {
        const skillComponents = [];
        for (const hero of heroes) {
          for (const skill of hero.skills) {
            const content =
              `[${skill.target}] ${skill.attribute} ${skill.value * 100}%`;
            const key =
              [hero.name, skill.target, skill.attribute, skill.value].join('-');
            const skillComponent = <div key={key}>
              {content}
            </div>;
            skillComponents.push(skillComponent);
          }
        }
        return skillComponents;
      },
    },
    {
      title: 'Elements',
      dataIndex: 'members',
      key: 'elements',
      render: (heroes: Array<Hero>) => {
        return heroes.map((hero: Hero, i: number) => {
          return <div key={i}>{hero.element}</div>;
        });
      },
    },
  ];
}

type Props = {
  location: Location,
  history: History
};

function TeamOptimizer({location, history}: Props) {
  const startTime = new Date().getTime();
  const {selectedHeroes, requiredHeroes, optionalHeroes} = params.get(location);
  const calculatedTeams = useMemo(() => {
    const {requiredHeroes, optionalHeroes} = params.get(location);
    const teams = [];
    generateTeams(requiredHeroes, optionalHeroes, teams, {
      members: [],
      calculation: [],
      atk: {basic: 0, adjusted: 0},
      hp: {basic: 0, adjusted: 0},
      def: {basic: 0, adjusted: 0},
      heal: {basic: 0, adjusted: 0},
    });
    calculateTeamAttributes(teams);
    return teams;
  }, [location]);

  return <div>
    <TeamOptimizerOptions/>
    <CalculationResultDialog
      isVisible={selectedHeroes.length !== 0}
      heroes={selectedHeroes}
      onOK={() => {
        params.update(history, {
            selectedHeroes: [],
            requiredHeroes,
            optionalHeroes
          }
        );
      }}
    />
    {process.env.NODE_ENV === 'development' &&
    <div>Processing time: {new Date().getTime() - startTime}</div>}
    <Table
      columns={getColumns(history, requiredHeroes, optionalHeroes)}
      dataSource={calculatedTeams}
    />
  </div>;
}

export default withRouter(TeamOptimizer);
